import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from '../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import {
  IApplicantActionPreferences,
  getApplicantActionPreferences,
  updateApplicantActionPreferences,
  updateJobApplicantStatus,
} from '../../../../../utils/api_service/JobOpenings';
import { SegmentType } from '../../../../../utils/types/JobOpenings';
import { RootState } from '../../../../../utils/redux_store/store';
import {JobOpeningsContext} from "../../../../../utils/contexts/JobOpening";

type AnalyticActionProps = {
  actionSelected: string[];
  setActionSelected: (value: string[]) => void;
  setSegmentedData: (value: SegmentType[][]) => void;
  defaultSegment: SegmentType[][];
  loading: boolean;
  allCandidates: any[]; // All candidates data
  setIsProfileModalOpen: (value: boolean) => void;
  setProfileModalCandidates: (value: any[]) => void;
  setProfileModalInitialIndex: (value: number) => void;
  setProfileModalTileName: (value: string) => void;
  handleCandidateClickFromChart?: (
    candidate: any,
    tileCandidates?: any[],
    tileName?: string
  ) => void;
};

export const AnalyticAction = ({
  actionSelected,
  setActionSelected,
  setSegmentedData,
  defaultSegment,
  loading,
  allCandidates,
  setIsProfileModalOpen,
  setProfileModalCandidates,
  setProfileModalInitialIndex,
  setProfileModalTileName,
  handleCandidateClickFromChart,
}: AnalyticActionProps) => {
  const { t } = useTranslation(NS.adminPages);
  const AA = ADMIN_PAGES_KEYS.jobOpenings.applicantAnalysis;
  const { id } = useParams<{ id: string }>();


  // Opting in means moving a candidate to Unsuccessful emails them straight
  // away instead of leaving them pending for a separate send. Per recruiter,
  // per opening, and only after they accept the warning.
  const [autoSendUnsuccessful, setAutoSendUnsuccessful] = useState(false);
  const [isAutoSendPromptOpen, setAutoSendPromptOpen] = useState(false);
  const [isSavingAutoSend, setSavingAutoSend] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getApplicantActionPreferences(id);
        if (!cancelled && res.ok) {
          const data: IApplicantActionPreferences = await res.json();
          setAutoSendUnsuccessful(Boolean(data.autoSendUnsuccessful));
        }
      } catch {
        if (!cancelled) setAutoSendUnsuccessful(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const saveAutoSend = async (enabled: boolean) => {
    if (!id) return;
    setSavingAutoSend(true);
    try {
      const res = await updateApplicantActionPreferences(id, {
        autoSendUnsuccessful: enabled,
        acknowledged: enabled,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not save the setting');
      setAutoSendUnsuccessful(enabled);
      toast.success(
        enabled
          ? t(AA.analyticAction.autoSendEnabled)
          : t(AA.analyticAction.autoSendDisabled),
        { position: 'bottom-center' },
      );
    } catch (e) {
      toast.error((e as Error).message, { position: 'bottom-center' });
    } finally {
      setSavingAutoSend(false);
      setAutoSendPromptOpen(false);
    }
  };

  // Turning it on needs the warning accepted; turning it off does not.
  const onAutoSendToggle = (enabled: boolean) => {
    if (enabled) setAutoSendPromptOpen(true);
    else saveAutoSend(false);
  };

  const { jobOpeningElements } = useContext(JobOpeningsContext);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const editApplicants = async (requestObject: {
    jobApplicantIds: string[];
    applicantStatus: string;
  }) => {
    try {
      const response = await updateJobApplicantStatus(id, requestObject);
      if (!response.ok) {
        toast.error('Error Changing applicant status', {
          position: 'bottom-center',
        });
        return;
      }
      setActionSelected([]);
      setSegmentedData(defaultSegment);
    } catch (error) {
      toast.error('Error Changing applicant status', {
        position: 'bottom-center',
      });
    }
  };

  const handleSelectAll = () => {
    const allCandidateIds = allCandidates.map(
      (candidate) => candidate.userId || candidate.id || candidate._id,
    );

    const areAllSelected = allCandidateIds.length > 0 &&
      allCandidateIds.every(id => actionSelected.includes(id));

    if (areAllSelected) {
      setActionSelected([]);
      toast.success(t(AA.analyticAction.deselectAll), {
        position: 'bottom-center',
      });
    } else {
      setActionSelected(allCandidateIds);
      toast.success(t(AA.analyticAction.selectAll), {
        position: 'bottom-center',
      });
    }
  };

  const handleOpenInTriage = () => {
    let candidatesToOpen = [];
    let candidateIds = [];

    if (actionSelected.length > 0) {
      candidateIds = actionSelected;
      candidatesToOpen = allCandidates.filter((candidate) =>
        actionSelected.includes(
          candidate.userId || candidate.id || candidate._id,
        ),
      );
    } else {
      candidateIds = allCandidates.map(
        (candidate) => candidate.userId || candidate.id || candidate._id,
      );
      candidatesToOpen = allCandidates;
      setActionSelected(candidateIds);
    }

    if (candidatesToOpen.length === 0) {
      toast.error(t(AA.analyticAction.noCandidates), { position: 'bottom-center' });
      return;
    }

    if (handleCandidateClickFromChart && candidatesToOpen.length > 0) {
      handleCandidateClickFromChart(
        candidatesToOpen[0],
        candidatesToOpen,
        'Selected Candidates Triage',
      );
    } else {
      try {
        if (typeof setProfileModalCandidates === 'function') {
          setProfileModalCandidates(candidatesToOpen);
        }
        if (typeof setProfileModalInitialIndex === 'function') {
          setProfileModalInitialIndex(0);
        }
        if (typeof setProfileModalTileName === 'function') {
          setProfileModalTileName('Selected Candidates Triage');
        }
        if (typeof setIsProfileModalOpen === 'function') {
          setIsProfileModalOpen(true);
        }
      } catch (error) {
        //
      }
    }

    toast.success(t(AA.analyticAction.openInTriage), { position: 'bottom-center' });
  };

  const handleOptionClick = (optionValue: string) => {
    if (optionValue === 'Open-in-Triage') {
      handleOpenInTriage();
    } else if (optionValue === 'Select-All') {
      handleSelectAll();
    } else {
      if (actionSelected.length > 0) {
        const requestObject = {
          jobApplicantIds: actionSelected,
          applicantStatus: optionValue,
        };
        editApplicants(requestObject).then(() =>
          toast.success(`Candidates moved to ${optionValue}!`, {
            position: 'bottom-center',
          }),
        );
      } else {
        toast.error(t(AA.analyticAction.noSelection), { position: 'bottom-center' });
      }
    }
  };

  const statusList = [
    {
      value: 'New',
      label: t(AA.analyticAction.moveToNew),
    },
    {
      value: 'Suitable',
      label: t(AA.analyticAction.moveToSuitable),
    },
    {
      value: 'Auto-Assessment',
      label: t(AA.analyticAction.moveToAutoAssess),
    },
    {
      value: 'Outstanding',
      label: t(AA.analyticAction.moveToOutstanding),
    },
    {
      value: 'Shortlisted',
      label: t(AA.analyticAction.moveToShortList),
    },
    {
      value: 'Unsuccessful-Pending',
      label: t(AA.analyticAction.moveToUnsuccessful),
    },
    ...(jobOpeningElements?.includes('Psychometric')
        ? [
          {
            value: 'Psychometric',
            label: 'Move selected to Psychometric',
          },
        ]
        : []),
    ...(jobOpeningElements?.includes('Wellness')
        ? [
          {
            value: 'Wellness',
            label: 'Move selected to Wellness',
          },
        ]
        : []),
    ...(jobOpeningElements?.includes('Agentic Assessment')
        ? [
          {
            value: 'Agentic-Assessment',
            label: 'Move selected to Agentic Assessment',
          },
        ]
        : []),
  ];

  const getIcon = (value: string) => {
    switch (value) {
      case 'New':
        return (
          <svg className='w-4 h-4 text-sky-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
          </svg>
        );
      case 'Suitable':
        return (
          <svg className='w-4 h-4 text-green-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        );
      case 'Auto-Assessment':
        return (
          <svg className='w-4 h-4 text-violet-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' />
          </svg>
        );
      case 'Outstanding':
        return (
          <svg className='w-4 h-4 text-amber-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.907c.961 0 1.36 1.24.588 1.81l-3.97 2.883a1 1 0 00-.364 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.971-2.883a1 1 0 00-1.18 0l-3.97 2.883c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.364-1.118L2.98 9.72c-.783-.57-.38-1.81.588-1.81h4.908a1 1 0 00.95-.69l1.519-4.674z' />
          </svg>
        );
      case 'Shortlisted':
        return (
          <svg className='w-4 h-4 text-indigo-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z' />
          </svg>
        );
      case 'Unsuccessful-Pending':
        return (
          <svg className='w-4 h-4 text-rose-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        );
      case 'Psychometric':
        return (
          <svg className='w-4 h-4 text-purple-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' />
          </svg>
        );
      case 'Wellness':
      case 'Agentic-Assessment':
        return (
            <svg className='w-4 h-4 text-purple-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' />
            </svg>
        );
      default:
        return (
          <svg className='w-4 h-4 text-slate-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2' />
          </svg>
        );
    }
  };

  const isAllSelected = allCandidates.length > 0 && allCandidates.every(id => actionSelected.includes(id));

  return (
    <div className='flex flex-col gap-6 w-full'>
      {/* Primary Control Actions */}
      <div className='grid grid-cols-2 gap-2'>
        <button
          onClick={handleSelectAll}
          disabled={loading || allCandidates.length === 0}
          className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-semibold rounded-xl border transition-all duration-200 ${
            isAllSelected
              ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
              : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 shadow-sm'
          } ${loading || allCandidates.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            {isAllSelected ? (
              <path strokeLinecap='round' strokeLinejoin='round' d='M19 7L12 14l-4-4' />
            ) : (
              <path strokeLinecap='round' strokeLinejoin='round' d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' />
            )}
          </svg>
          <span className='truncate'>
            {isAllSelected ? t(AA.analyticAction.deselectAll) : t(AA.analyticAction.selectAll)}
          </span>
        </button>

        <button
          onClick={handleOpenInTriage}
          disabled={loading || allCandidates.length === 0}
          className={`flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-semibold rounded-xl border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-all duration-200 ${
            loading || allCandidates.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
            <path strokeLinecap='round' strokeLinejoin='round' d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
          </svg>
          <span className='truncate'>{t(AA.analyticAction.openInTriage)}</span>
        </button>
      </div>

      {/* Move Status List */}
      <div>
        <h3 className='text-[10px] font-bold text-slate-400 tracking-wider uppercase mb-2 px-1'>
          Change Status
        </h3>
        <div className={`flex flex-col gap-1.5 ${loading ? 'animate-pulse' : ''}`}>
          {loading
            ? Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className='h-11 bg-slate-100 rounded-xl w-full' />
              ))
            : statusList.map((status, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(status.value)}
                  disabled={loading}
                  className='group flex items-center justify-between w-full px-3 py-2 text-left rounded-xl bg-white border border-slate-100 shadow-sm hover:border-emerald-200 hover:bg-emerald-50/20 transition-all duration-200'
                >
                  <div className='flex items-center gap-2.5 min-w-0'>
                    <span className='flex-shrink-0 w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center group-hover:bg-white shadow-sm border border-slate-100/50 transition-colors'>
                      {getIcon(status.value)}
                    </span>
                    <span className='text-[13px] font-semibold text-slate-700 group-hover:text-slate-900 truncate transition-colors'>
                      {status.label}
                    </span>
                  </div>
                  <span className='text-slate-300 group-hover:text-emerald-500 transform group-hover:translate-x-0.5 transition-all flex-shrink-0'>
                    <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
                    </svg>
                  </span>
                </button>
              ))}
        </div>
      </div>

      {/* Automatic unsuccessful emails -- opt in, per recruiter, per opening */}
      <div className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
        <label className='flex cursor-pointer items-start gap-3'>
          <input
            type='checkbox'
            className='mt-0.5 h-4 w-4 accent-emerald-700'
            checked={autoSendUnsuccessful}
            disabled={isSavingAutoSend}
            onChange={(e) => onAutoSendToggle(e.target.checked)}
          />
          <span className='text-xs leading-relaxed'>
            <span className='block font-bold text-slate-900'>
              {t(AA.analyticAction.autoSendLabel)}
            </span>
            <span className='mt-0.5 block text-slate-500'>
              {t(AA.analyticAction.autoSendHint)}
            </span>
          </span>
        </label>
      </div>

      {/* The warning is accepted once, before any candidate is auto-emailed */}
      {isAutoSendPromptOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4'>
          <div className='w-full max-w-md rounded-2xl bg-white p-6 shadow-xl'>
            <h3 className='text-base font-bold text-slate-900'>
              {t(AA.analyticAction.autoSendWarningTitle)}
            </h3>
            <p className='mt-2 text-sm leading-relaxed text-slate-600'>
              {t(AA.analyticAction.autoSendWarningBody)}
            </p>
            <div className='mt-5 flex justify-end gap-2'>
              <button
                type='button'
                disabled={isSavingAutoSend}
                onClick={() => setAutoSendPromptOpen(false)}
                className='rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
              >
                {t(AA.analyticAction.autoSendWarningCancel)}
              </button>
              <button
                type='button'
                disabled={isSavingAutoSend}
                onClick={() => saveAutoSend(true)}
                className='rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:opacity-50'
              >
                {t(AA.analyticAction.autoSendWarningConfirm)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions Card */}
      <div className='flex gap-3 bg-amber-50/50 border border-amber-100 rounded-2xl p-4 text-amber-800 shadow-sm'>
        <span className='flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-amber-600'>
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        </span>
        <div className='flex-1 text-xs leading-relaxed text-amber-900'>
          <span className='font-bold block mb-1 text-amber-950'>{t(AA.analyticAction.instructionsLabel)}</span>
          Click on a box in the grid containing candidates, then select the status you wish to move them to. Use &ldquo;Select All&rdquo; to select all candidates or &ldquo;Open in Triage&rdquo; to view profile cards.
        </div>
      </div>
    </div>
  );
};
